import { Buffer } from "buffer";
import { sha256 } from "@noble/hashes/sha256";
import bs58 from "bs58";
import { ComputeBudgetProgram, PublicKey, SystemProgram, Transaction, TransactionInstruction } from "@solana/web3.js";
import { assertWalletMessage } from "./walletMessage.js";
export const MATERIAL_COLLECTION_MAX_ITEMS = 20;
export const MATERIAL_DELIVERY_ATTEMPTS = 3;
export const MATERIAL_DELIVERY_FEE_LAMPORTS = 50_000;
export const MATERIAL_REFUND_FEE_LAMPORTS = 5_000;
const memo = new PublicKey("MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr");
export function validateMaterialCollection(m) {
    if (m.version !== "SERVER_COLLECTION_V1" || !/^[a-zA-Z0-9_-]{8,128}$/.test(m.collectionId)
        || !/^[1-9][0-9]{0,15}$/.test(m.budgetLamports) || BigInt(m.budgetLamports) > 100000000n
        || !Array.isArray(m.items) || m.items.length < 1 || m.items.length > MATERIAL_COLLECTION_MAX_ITEMS
        || new Set(m.items.map(i => i.claimId)).size !== m.items.length)
        throw new Error("material_collection_invalid");
    const keys = [m.wallet, m.program, m.refineryAuthority, m.costWallet].map(k => new PublicKey(k).toBase58());
    if (new Set(keys).size !== keys.length)
        throw new Error("material_collection_payer_invalid");
    for (const i of m.items) {
        new PublicKey(i.mint);
        if (!/^[a-zA-Z0-9_-]{1,128}$/.test(i.claimId) || !/^[a-z][a-z0-9_]{0,63}$/.test(i.resourceId)
            || !/^[1-9][0-9]{0,19}$/.test(i.amountBaseUnits) || BigInt(i.amountBaseUnits) > 18446744073709551615n)
            throw new Error("material_collection_item_invalid");
    }
}
export function materialCollectionDigest(m) {
    validateMaterialCollection(m);
    // Explicit field order also discards object insertion-order differences at API boundaries.
    return Buffer.from(sha256(Buffer.from(JSON.stringify([m.version, m.collectionId, m.wallet, m.program,
        m.refineryAuthority, m.costWallet, m.budgetLamports, m.items.map(i => [i.claimId, i.resourceId, i.mint, i.amountBaseUnits])])))).toString("hex");
}
export function buildMaterialCollectionPayment(m, blockhash) {
    const digest = materialCollectionDigest(m);
    return new Transaction({ feePayer: new PublicKey(m.wallet), recentBlockhash: blockhash }).add(ComputeBudgetProgram.setComputeUnitLimit({ units: 100_000 }), ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 100_000 }), SystemProgram.transfer({ fromPubkey: new PublicKey(m.wallet), toPubkey: new PublicKey(m.costWallet), lamports: BigInt(m.budgetLamports) }), new TransactionInstruction({ programId: memo, keys: [], data: Buffer.from(`megacorp:materials:v1:${digest}`) }));
}
export function validateMaterialCollectionPayment(encoded, m, prepared) {
    const bytes = Buffer.from(encoded, "base64");
    if (bytes.length > 1232 || bytes.toString("base64") !== encoded)
        throw new Error("material_payment_wire_invalid");
    const tx = Transaction.from(bytes), original = Transaction.from(Buffer.from(prepared, "base64"));
    const expected = buildMaterialCollectionPayment(m, original.recentBlockhash);
    if (!expected.serializeMessage().equals(original.serializeMessage()))
        throw new Error("material_payment_preparation_invalid");
    assertWalletMessage(expected.compileMessage(), tx.compileMessage());
    if (tx.signatures.length !== 1 || tx.signatures[0].publicKey.toBase58() !== m.wallet || !tx.verifySignatures())
        throw new Error("material_payment_signature_invalid");
    return bs58.encode(tx.signature);
}
